import React, { useMemo } from "react";
import styles from "./style.module.scss";
import { useSelector } from "react-redux";
import { RootState } from "../../../../state";
import { lapData } from "../../../../types/virtualLoops";
import { Scatter } from "react-chartjs-2";
import { getPreferredFormattedTimeSeconds } from "../../../../utils/preferences";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  TooltipItem,
} from "chart.js";
import zoomPlugin from "chartjs-plugin-zoom";

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  zoomPlugin,
);

function LapTimePlot() {
  const filteredData = useSelector(
    (state: RootState) => state.virtualLoops.filteredData,
  );

  const generation = useSelector(
    (state: RootState) => state.virtualLoops?.generation || "Gen3",
  );
  const highPowerMode = generation === "Gen4" ? "600" : "350";

  const chartData = useMemo(() => {
    if (!filteredData || Object.keys(filteredData).length === 0) {
      return {
        labels: [],
        datasets: [],
      };
    }
    const allLaps: lapData[] = [];
    Object.keys(filteredData).forEach((sessionKey) => {
      allLaps.push(...filteredData[sessionKey]);
    });

    const groupedData: { [key: string]: lapData[] } = {};
    allLaps.forEach((lap) => {
      const key = lap.powerMode;
      if (!groupedData[key]) {
        groupedData[key] = [];
      }
      groupedData[key].push(lap);
    });

    const datasets = Object.entries(groupedData).map(([key, laps]) => {
      const isHighPower = key === highPowerMode;
      const color = isHighPower ? "#ff00f2" : "#00ff59";
      const sortedLaps = laps
        .filter((lap) => lap.real)
        .sort(
          (a, b) =>
            new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
        );

      return {
        label: isHighPower ? "High Power" : "Low Power",
        data: sortedLaps.map((lap) => ({
          x: new Date(lap.startTime),
          y: lap.lapTime,
        })),
        backgroundColor: color,
        borderColor: color,
        pointRadius: 4,
        pointBorderColor: "#ffffff",
        pointBorderWidth: 1,
      };
    });

    return { datasets };
  }, [filteredData, highPowerMode]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false as const,
    plugins: {
      tooltip: {
        callbacks: {
          label: (context: TooltipItem<"scatter">) => {
            return `${
              context.dataset.label || ""
            }: ${getPreferredFormattedTimeSeconds(context.parsed.y)}`;
          },
        },
      },
      datalabels: {
        display: false,
      },
      legend: {
        display: true,
        position: "bottom" as const,
        labels: {
          color: "#ffffff",
          font: {
            weight: "bold" as const,
            size: 11,
          },
          usePointStyle: true,
          pointStyle: "circle",
          boxWidth: 8,
          boxHeight: 8,
          padding: 15,
        },
      },
      title: {
        display: true,
        text: "Lap Times",
        color: "#ffffff",
        font: { size: 18, weight: "bold" as const },
      },
      zoom: {
        zoom: {
          wheel: {
            enabled: false,
          },
          pinch: {
            enabled: false,
          },
          mode: "xy" as const,
        },
        pan: {
          enabled: false,
          mode: "xy" as const,
        },
      },
    },
    scales: {
      x: {
        type: "time" as const,
        time: {
          displayFormats: {
            hour: "MMM DD HH:mm",
          },
        },
        grid: { color: "rgba(255, 255, 255, 0.1)" },
        ticks: { color: "#ffffff" },
      },
      y: {
        title: {
          display: true,
          text: "Lap Time",
          color: "#ffffff",
        },
        grid: { color: "rgba(255, 255, 255, 0.1)" },
        ticks: {
          color: "#ffffff",
          callback: (value: string | number) =>
            getPreferredFormattedTimeSeconds(Number(value), false),
        },
      },
    },
  };

  return (
    <div className={styles.container}>
      <Scatter data={chartData} options={chartOptions} />
    </div>
  );
}

export default LapTimePlot;
